import { alpha, Box, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { get, isEmpty, map, upperFirst } from 'lodash';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import { TREEMAP_PALLET_COLOR_HS_CODE } from 'src/components/HighChart/Treemap/config';
import ProductBreakdownSkeleton from 'src/features/CompanyDetail/HSCode/ProductBreakdownSkeleton';
import NoDataSearchCompany from 'src/features/CompanyProfiles/NoDataSearchCompany';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryCompanyTradeDataHSCode } from 'src/services/queries/useQueryCompanyTradeData';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import {
  KeyFilterChartCTDRelationshipView,
  KeyFilterChartTradeDataUnitBy,
  TypeBuyerSupplier,
} from 'src/utils/constants/enum';
import Top10HSCode from "src/features/CompanyDetail/HSCode/Top10HSCode";

const ProductBreakdown = () => {
  const [unitBy, setUnitBy] = useState(KeyFilterChartTradeDataUnitBy.bol);
  const { id } = useParams();
  const { end_date, start_date, viewedAsType } = useQueryParams();
  const [relationshipWithCurrentCompany, setRelationshipWithCurrentCompany] = useState<any>(
    viewedAsType === TypeBuyerSupplier.supplier
      ? KeyFilterChartCTDRelationshipView.buyer
      : KeyFilterChartCTDRelationshipView.supplier,
  );
  console.log('relationshipWithCurrentCompany: ', relationshipWithCurrentCompany)
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: start_date ?? dayjs(runtimeConfig?.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    toTime: end_date ?? dayjs(runtimeConfig?.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
  });
  const params = {
    start_date: selectedRangeTime.fromTime,
    end_date: selectedRangeTime.toTime,
    offset: 0,
    limit: 9,
    company_id: id,
    order: 'desc',
    sort_by: unitBy,
    is_buyer: relationshipWithCurrentCompany === KeyFilterChartCTDRelationshipView.supplier,
  };

  const { data, isFetching } = useQueryCompanyTradeDataHSCode(params);

  const renderChart = () => {
    if (isFetching) return <ProductBreakdownSkeleton />;

    if (!isFetching && isEmpty(data?.data?.children))
      return (
        <WhiteBoxContent sx={{ display: 'grid', placeItems: 'center' }} height={600}>
          <NoDataSearchCompany
            title={`This company does not ${relationshipWithCurrentCompany === TypeBuyerSupplier.buyer ? 'export' : 'import'} , therefor it has no information`}
            hideTip
            description=""
          />
        </WhiteBoxContent>
      );

    return map(data?.data?.children, (item, i) => (
      <Grid item xs={4} key={i}>
        <HSCodeTree unitBy={unitBy} level={0} data={item} parentColorIndex={i} />
      </Grid>
    ));
  };

  return (
    <Stack gap={3}>
      <WhiteBoxContent>
        <FilterChart
          selectedRelationship={relationshipWithCurrentCompany}
          onSelectRelationship={(value) => setRelationshipWithCurrentCompany(value)}
          onSelectUnitBy={(key) => setUnitBy(key as KeyFilterChartTradeDataUnitBy)}
          selectedUnitBy={unitBy}
          title="Product Breakdown (H.S. Code)"
          selectedRangeTime={selectedRangeTime}
          onSelectTimeRange={(arg) => setSelectedRangeTime(arg)}
        />

        <Box mt={3} />

        <Grid container spacing={3}>
          {renderChart()}
        </Grid>
      </WhiteBoxContent>
      <Top10HSCode
        start_date={selectedRangeTime.fromTime}
        end_date={selectedRangeTime.toTime}
        relationshipWithCurrentCompany={relationshipWithCurrentCompany}
      />
    </Stack>

  );
};

export default ProductBreakdown;

const HSCodeTree = ({
  data,
  level = 0,
  parentColorIndex = 0,
  unitBy,
}: {
  data: TypeCompanyTradeDataHSCodeChildren;
  level: number;
  parentColorIndex: number;
  unitBy: KeyFilterChartTradeDataUnitBy;
}) => {
  const backgroundColor = alpha(TREEMAP_PALLET_COLOR_HS_CODE[parentColorIndex], 0.2 * (level + 1));
  return (
    <Box
      borderRadius={2}
      mb={1}
      display="flex"
      flexDirection="column"
      flex={1}
      height={'100%'}
      bgcolor={backgroundColor}
      p={1}
    >
      <Tooltip
        placement="top"
        title={
          <Stack spacing={1} p={1}>
            <Typography color={'text.secondary'} variant="subtitle1">
              HS Code <span style={{ color: 'white' }}>{data.hscode}</span>
            </Typography>
            <Typography color={'text.secondary'} variant="subtitle1">
              {upperFirst(unitBy)} <span style={{ color: 'white' }}>{get(data, unitBy)}</span>
            </Typography>
            <Divider />
            <Typography variant="body2" color={'inherit'}>
              {data.description}
            </Typography>
          </Stack>
        }
      >
        <Typography mb={1} textAlign="center" variant="subtitle1" color={level === 0 ? 'text.primary' : 'white'}>
          {data.hscode}
        </Typography>
      </Tooltip>
      {map(data.children, (child, i) => (
        <HSCodeTree
          unitBy={unitBy}
          key={child.hscode}
          data={child}
          level={level + 1}
          parentColorIndex={parentColorIndex}
        />
      ))}
    </Box>
  );
};
